import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import IconOnlyChip from './IconOnlyChip';
import IconTextChip from './IconTextChip';
import { useAuth } from './AuthContext';
import { deleteComment, upvoteComment, downvoteComment, reportComment } from '../services/api';
import EditCommentDialog from './EditCommentDialog';
import getUserDisplayName from '../utils/getUserDisplayName';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import ActionReasonDialog from './ActionReasonDialog';
import { POST_REPORT_REASONS } from '../utils/moderationReasons';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import { isCommentEdited } from '../utils/isEdited';
import EditedTag from './EditedTag';

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const timeAgo = (dateString) => {
  const now = new Date();
  const past = new Date(dateString);
  const diffInSeconds = Math.floor((now - past) / 1000);
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function CommentCard({ comment, disableVoting = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, emailVerified } = useAuth();
  const verifiedUser = user && emailVerified;
  const [editOpen, setEditOpen] = useState(false);
  const [commentData, setCommentData] = useState(comment);
  const [upvotes, setUpvotes] = useState(comment.upvotes || 0);
  const [downvotes, setDownvotes] = useState(comment.downvotes || 0);
  const [upvoted, setUpvoted] = useState(comment.upvoted || false);
  const [downvoted, setDownvoted] = useState(comment.downvoted || false);
  const [reported, setReported] = useState(comment.reported || false);
  const [reportOpen, setReportOpen] = useState(false);
  useEffect(() => {
    setCommentData(comment);
    setUpvotes(comment.upvotes || 0);
    setDownvotes(comment.downvotes || 0);
    setUpvoted(comment.upvoted || false);
    setDownvoted(comment.downvoted || false);
    setReported(comment.reported || false);
  }, [comment]);
  const author = commentData.created_by || {};
  const thread = commentData.thread || {};
  const isAuthor = user?.id === author.id;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const handleAuthorClick = (e) => {
    e.stopPropagation();
    const name = getUserDisplayName(author);
    if (!name) return;
    const path = `/community/user/${encodeURIComponent(name)}`;
    if (location.pathname !== path) {
      navigate(path, { state: { from: location.pathname } });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteComment(commentData.id);
      setDeleted(true);
    } catch (err) {
      console.error('Failed to delete comment', err);
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleUpvote = async () => {
    try {
      const res = await upvoteComment(commentData.id);
      const { upvotes, downvotes, upvoted, downvoted } = res.data;
      setUpvotes(upvotes);
      setDownvotes(downvotes);
      setUpvoted(upvoted);
      setDownvoted(downvoted);
    } catch (err) {
      console.error('Upvote error:', err);
    }
  };

  const handleDownvote = async () => {
    try {
      const res = await downvoteComment(commentData.id);
      const { upvotes, downvotes, upvoted, downvoted } = res.data;
      setUpvotes(upvotes);
      setDownvotes(downvotes);
      setUpvoted(upvoted);
      setDownvoted(downvoted);
    } catch (err) {
      console.error('Downvote error:', err);
    }
  };

  const handleReport = async (reason) => {
    try {
      await reportComment(commentData.id, reason);
      setReported(true);
    } catch (err) {
      console.error('Failed to report comment', err);
    } finally {
      setReportOpen(false);
    }
  };

  if (deleted) return null;

  return (
    <>
    <Card sx={{ mb: 2, border: '1px solid #ddd', boxShadow: 'none', '&:hover': { boxShadow: 2 } }}>
      <CardContent
        sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconTextChip
            icon={
              <Avatar
                src={author.avatar || undefined}
                alt={author.display_name || author.username || 'Anonymous'}
                sx={{ width: 24, height: 24 }}
              >
                {!author.avatar && (author.display_name || author.username || 'A')[0]}
              </Avatar>
            }
            label={author.display_name || author.username || 'Anonymous'}
            onClick={handleAuthorClick}  sx={{pl:0}}
          />
          {verifiedUser && (
            <IconTextChip
              icon={reported ? <FlagIcon /> : <OutlinedFlagIcon />}
              label={reported ? 'Reported' : 'Report'}
              color="error"
              onClick={() => setReportOpen(true)}
              disabled={reported}
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          color="text.primary"
          sx={{ my: 1, mx: 0.5 }}
        >
          {stripHtml(commentData.content)}
          {isCommentEdited(commentData) &&
            !editOpen &&
            !['[deleted]', '[removed]'].includes((commentData.content || '').trim()) && (
              <>
                {' '}
                <EditedTag />
              </>
            )}
        </Typography>
        <Box
          onClick={() =>
            navigate(`/community/threads/${thread.id}`, {
              state: { from: location.pathname },
            })
          }
          sx={(theme) => ({
            mt: 1,
            p: 1,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: theme.shadows[0],
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#f9f9f9' },
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {thread.title}
            </Typography>
            {thread.topicName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={thread.topicName}
                  size="small"
                  component={Link}
                  to={`/community/topic/${encodeURIComponent(thread.topicName)}`}
                  state={{ from: location.pathname }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    fontWeight: 'bold',
                    border: `2px solid ${thread.topicColor}`,
                    backgroundColor: `${thread.topicColor}20`,
                  }}
                />
                {thread.subtopicName && (
                  <>
                    <ChevronRightRoundedIcon fontSize="small" color="action" />
                    {thread.subtopicId ? (
                      <Chip
                        label={thread.subtopicName}
                        size="small"
                        component={Link}
                        to={`/community/topic/${encodeURIComponent(
                          thread.topicName
                        )}?subs=${thread.subtopicId}`}
                        state={{ from: location.pathname }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <Chip label={thread.subtopicName} size="small" />
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <IconTextChip
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label={
              new Date() - new Date(commentData.created_at) > 86400000
                ? formatDate(commentData.created_at)
                : timeAgo(commentData.created_at)
            }
            color="default"
          />
          <IconTextChip
            icon={upvoted ? <ThumbUp /> : <ThumbUpOffAltIcon />}
            label={upvotes}
            color="success"
            onClick={verifiedUser ? handleUpvote : undefined}
            disabled={disableVoting || !verifiedUser}
          />
          <IconTextChip
            icon={downvoted ? <ThumbDown /> : <ThumbDownOffAltIcon />}
            label={downvotes}
            color="success"
            onClick={verifiedUser ? handleDownvote : undefined}
            disabled={disableVoting || !verifiedUser}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {verifiedUser && isAuthor && (
              <>
                <IconOnlyChip
                  icon={<EditOutlinedIcon />}
                  color="success"
                  onClick={() => setEditOpen(true)}
                />
                <IconOnlyChip
                  icon={<DeleteOutlineOutlinedIcon />}
                  color="error"
                  onClick={() => setConfirmOpen(true)}
                />
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
      <DialogTitle>Delete Comment?</DialogTitle>
      <DialogContent>
        <Typography>Are you sure you want to delete this comment?</Typography>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={() => setConfirmOpen(false)} text="Cancel" color="success" />
        <FilledButton onClick={handleDelete} text="Delete" color="error" />
      </DialogActions>
    </Dialog>
    <EditCommentDialog
      open={editOpen}
      comment={commentData}
      onClose={() => setEditOpen(false)}
      onUpdated={(content) =>
        setCommentData((c) => ({ ...c, content }))
      }
    />
    <ActionReasonDialog
      open={reportOpen}
      onClose={() => setReportOpen(false)}
      title="Report Comment"
      reasons={POST_REPORT_REASONS}
      onConfirm={handleReport}
    />
    </>
  );
}
